import React from "react";
import { IndexForms, NodeEdge } from "@hex-insights/app-modules";
import {
	addTimeToDate,
	Button,
	ClassNameProps,
	Column,
	dateTrunc,
	DistributiveOmit,
	filterMap,
	Heading,
	Modal,
	ModalProps,
	omitKeys,
	RequiredKeys,
	Row,
	stringToLocalDate,
	StyleProps,
} from "@hex-insights/core";
import {
	CheckBoxInput,
	DateTimeField,
	FieldIf,
	Form,
	FormState,
	FormSubmitFunction,
	FormType,
	Label,
	SelectField,
	useFormState,
	UseFormStateArgs,
	useInternalField,
	useOnSuccess,
} from "@hex-insights/forms";
import { Card } from "../../../Components/Card";
import { BigEventCalendar } from "../../../Components/EventCalendar";
import { FieldToggle } from "../../../Components/FieldToggle";
import { Icon } from "../../../Components/Icon";
import {
	AcademicTerm,
	AcademicTermsDatesListQuery,
	ClassYearLookupQuery,
	CourseSection,
	CourseSectionFilterInput,
	CourseSectionSummaryListQuery,
	HomeRoomSection,
	isBetweenTimes,
	Student,
	StudentAcademicTermEnrollment,
	useAcademicTermDatesQuery,
	useClassYearLookupQuery,
	useCourseSectionSummaryListLazyQuery,
	useHomeRoomSectionSelectQuery,
} from "../../../Utilities";
import { CourseSectionComponents } from "../../Components";
import { AcademicTermSelect, HomeRoomSectionSelect, StudentFormValues } from "../../FormUtilities";
import { ClassYearOrderFormValues, CourseSectionOrderFormValues, HomeRoomSectionOrderFormValues } from "../../Order";
import { AcademicTermUtilities, CourseSectionPeriodUtilities } from "../../Utilities";
import { BaseFormProps } from "../shared";
import * as Field from "./Field";
import styles from "./enrollment-form.module.css";

export type EnrollmentFormValues = {
	lateStartDate: string | null;
	earlyEndDate: string | null;
	academicTermID: AcademicTerm["id"] | null;
	graduationYear: StudentFormValues.Create["graduationYear"];
	homeRoomSectionID: HomeRoomSection["id"] | null;
	courseSectionIDs: CourseSection["id"][];
};

export const initialEnrollmentFormValues: EnrollmentFormValues = {
	lateStartDate: null,
	earlyEndDate: null,
	academicTermID: null,
	graduationYear: null,
	homeRoomSectionID: null,
	courseSectionIDs: [],
};

export type UseCreateFormStateArgs = DistributiveOmit<UseFormStateArgs<EnrollmentFormValues>, "initialFormValues"> & {
	initialFormValues?: Partial<EnrollmentFormValues>;
};

export function useCreateFormState({
	initialFormValues,
	formType = FormType.Create,
	...args
}: UseCreateFormStateArgs = {}) {
	return useFormState({
		initialFormValues: () => ({ ...initialEnrollmentFormValues, ...initialFormValues }),
		formType,
		...args,
	});
}

export type EnrollmentOnExistingFormValues = Omit<EnrollmentFormValues, "academicTermID">;

export const initialEnrollmentOnExistingFormValues: EnrollmentOnExistingFormValues = omitKeys(
	initialEnrollmentFormValues,
	["academicTermID"],
);

export function toEnrollmentOnExistingFormValues(
	student: Pick<Student, "graduationYear">,
	academicTermEnrollment: Pick<StudentAcademicTermEnrollment, "lateStartDate" | "earlyEndDate"> | null,
	homeRoomSectionEnrollment: { homeRoomSection: Pick<HomeRoomSection, "id"> } | null,
	courseSectionEnrollmentEdges: NodeEdge<{ courseSection: Pick<CourseSection, "id"> }>[],
): EnrollmentOnExistingFormValues {
	return {
		lateStartDate: academicTermEnrollment?.lateStartDate ?? null,
		earlyEndDate: academicTermEnrollment?.earlyEndDate ?? null,
		graduationYear: student.graduationYear,
		homeRoomSectionID: homeRoomSectionEnrollment?.homeRoomSection.id ?? null,
		courseSectionIDs: courseSectionEnrollmentEdges.map((e) => e.node.courseSection.id),
	};
}

export type UseEnrollmentOnExistingFormStateArgs = DistributiveOmit<
	UseFormStateArgs<EnrollmentOnExistingFormValues>,
	"initialFormValues"
> & {
	initialFormValues?: Partial<EnrollmentOnExistingFormValues>;
};

export function useEnrollmentOnExistingFormState({
	initialFormValues,
	formType = FormType.Create,
	...args
}: UseEnrollmentOnExistingFormStateArgs = {}) {
	return useFormState({
		initialFormValues: () => ({ ...initialEnrollmentOnExistingFormValues, ...initialFormValues }),
		formType,
		...args,
	});
}

export type ControlledCreateProps = Omit<CreateProps, "formState"> & {
	formStateArgs?: UseCreateFormStateArgs;
};

export function ControlledCreate({ formStateArgs, ...props }: ControlledCreateProps) {
	const formState = useCreateFormState(formStateArgs);

	return <Create formState={formState} {...props} />;
}

export type CreateProps = {
	formState: FormState<EnrollmentFormValues>;
	onSubmit: FormSubmitFunction<EnrollmentFormValues>;
	onSuccess: (formState: FormState<EnrollmentFormValues>) => void;
	formNameSuffix?: string;
	columnClassName?: string;
	columnStyle?: React.CSSProperties;
	variant?: "create" | "create-update";
} & BaseFormProps<EnrollmentFormValues> &
	Partial<ClassNameProps & StyleProps>;

export function Create({
	formState,
	onSubmit,
	onSuccess,
	formNameSuffix,
	variant = "create",
	columnClassName,
	columnStyle,
	...props
}: CreateProps) {
	const formName = "student.enrollment.create" + (formNameSuffix ? "." + formNameSuffix : "");

	useOnSuccess(formState, onSuccess);

	const { lateStartDate, academicTermID } = formState.formValues;
	const { academicTermID: setAcademicTermID } = formState.formSetFunctions;

	const {
		loading: loadingAcademicTerms,
		academicTerms,
		selectedAcademicTerm: closestAcademicTerm,
	} = AcademicTermUtilities.useAcademicTermsNavigation({ defaultSelectedTermFallback: "future" });
	React.useEffect(() => {
		if (closestAcademicTerm) {
			setAcademicTermID(closestAcademicTerm.id);
		}
	}, [setAcademicTermID, closestAcademicTerm]);
	const academicTermOptions = React.useMemo(
		() => AcademicTermSelect.toOptions(academicTerms.map((e) => ({ node: e }))),
		[academicTerms],
	);
	const academicTerm = academicTerms.find((e) => e.id === academicTermID) ?? null;

	const { lateStartDate: lateStartDateWasChanged } = formState.formChanged;
	React.useEffect(() => {
		if (lateStartDate && lateStartDateWasChanged) {
			const lateStartDateDate = stringToLocalDate(lateStartDate, "day");
			const academicTerm = academicTerms.find((e) =>
				isBetweenTimes(lateStartDateDate, stringToLocalDate(e.startDate, "day"), stringToLocalDate(e.endDate, "day")),
			);
			if (academicTerm) {
				setAcademicTermID(academicTerm.id);
			}
		}
	}, [lateStartDate, lateStartDateWasChanged, academicTerms, setAcademicTermID]);

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column className={columnClassName} style={columnStyle}>
				<SelectField
					formState={formState}
					name="academicTermID"
					isLoading={loadingAcademicTerms}
					options={academicTermOptions}
					blankValue={null}
				/>

				<CommonFields
					formState={formState}
					academicTermID={academicTermID}
					academicTermIDWasChanged={formState.formChanged.academicTermID}
					academicTerm={academicTerm}
					variant={variant}
				/>
			</Form.Column>
		</Form>
	);
}

export type ControlledOnExistingProps = Omit<OnExistingProps, "formState"> & {
	formStateArgs?: UseCreateFormStateArgs;
};

export function ControlledOnExisting({ formStateArgs, ...props }: ControlledOnExistingProps) {
	const formState = useEnrollmentOnExistingFormState(formStateArgs);

	return <OnExisting formState={formState} {...props} />;
}

export type OnExistingProps = {
	studentID: Student["id"];
	academicTermID: AcademicTerm["id"];
	formState: FormState<EnrollmentOnExistingFormValues>;
	onSubmit: FormSubmitFunction<EnrollmentOnExistingFormValues>;
	onSuccess: (formState: FormState<EnrollmentOnExistingFormValues>) => void;
	formNameSuffix?: string;
	columnClassName?: string;
	columnStyle?: React.CSSProperties;
} & BaseFormProps<EnrollmentOnExistingFormValues> &
	Partial<ClassNameProps & StyleProps>;

export function OnExisting({
	studentID,
	academicTermID,
	formState,
	onSubmit,
	onSuccess,
	formNameSuffix,
	columnClassName,
	columnStyle,
	...props
}: OnExistingProps) {
	const formName = `student.enrollment.${studentID}.${academicTermID}` + (formNameSuffix ? "." + formNameSuffix : "");

	useOnSuccess(formState, onSuccess);

	const { data: academicTermData } = useAcademicTermDatesQuery({
		variables: { id: academicTermID },
	});
	const academicTerm = academicTermData?.academicTerm ?? null;

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column className={columnClassName} style={columnStyle}>
				<CommonFields
					formState={formState}
					academicTermID={academicTermID}
					academicTerm={academicTerm}
					variant="on-existing"
				/>
			</Form.Column>
		</Form>
	);
}

type CommonFieldsProps = {
	formState: FormState<Omit<EnrollmentFormValues, "academicTermID">>;
	academicTermID: EnrollmentFormValues["academicTermID"];
	academicTermIDWasChanged?: boolean;
	academicTerm: AcademicTermsDatesListQuery["academicTermConnection"]["edges"][0]["node"] | null;
	variant: "create" | "create-update" | "on-existing";
};

function CommonFields({
	formState,
	academicTermID,
	academicTermIDWasChanged,
	academicTerm,
	variant,
}: CommonFieldsProps) {
	const { graduationYear, homeRoomSectionID } = formState.formValues;

	const { someHomeRoomSections, loadingHomeRoomSections, homeRoomSectionOptions, noHomeRoomSections, classYear } =
		useEnrollmentForm({ academicTermID, academicTermIDWasChanged: academicTermIDWasChanged, formState });

	// to prevent the <FieldIf> components from clearing initial form values on initial home room sections load
	if (
		(variant === "on-existing" || variant === "create-update") &&
		!formState.formChanged.graduationYear &&
		loadingHomeRoomSections
	) {
		return <div>Fetching information...</div>;
	}

	return (
		<React.Fragment>
			<FieldToggle
				formState={formState}
				name="lateStartDate"
				buttonVariant="tertiary"
				buttonSize="small"
				buttonChildren="Starting Late"
				initialIsOpen={!!formState.formValues.lateStartDate}
			>
				<DateTimeField formState={formState} name="lateStartDate" optional />
			</FieldToggle>
			<FieldToggle
				formState={formState}
				name="earlyEndDate"
				buttonVariant="tertiary"
				buttonSize="small"
				buttonChildren="Leaving Early"
				initialIsOpen={!!formState.formValues.earlyEndDate}
			>
				<DateTimeField formState={formState} name="earlyEndDate" optional />
			</FieldToggle>

			<Field.GraduationYear formState={formState} />

			<FieldIf
				formState={formState}
				name="homeRoomSectionID"
				condition={!!academicTermID && !!graduationYear && someHomeRoomSections}
			>
				<SelectField
					formState={formState}
					name="homeRoomSectionID"
					label="Home Room"
					isLoading={loadingHomeRoomSections}
					options={homeRoomSectionOptions}
					blankValue={null}
				/>
			</FieldIf>

			<FieldIf
				formState={formState}
				name="courseSectionIDs"
				condition={!!academicTermID && !!graduationYear && (!!homeRoomSectionID || noHomeRoomSections)}
			>
				<CourseSectionsField
					academicTermID={academicTermID}
					formState={formState}
					academicTerm={academicTerm}
					variant={variant}
					classYear={classYear}
					style={{ width: "100%" }}
				/>
			</FieldIf>
		</React.Fragment>
	);
}

type UseEnrollmentFormArgs = {
	academicTermID: EnrollmentFormValues["academicTermID"];
	academicTermIDWasChanged?: boolean;
	formState: FormState<Omit<EnrollmentFormValues, "academicTermID">>;
};

function useEnrollmentForm({ academicTermID, academicTermIDWasChanged = false, formState }: UseEnrollmentFormArgs) {
	const { graduationYear } = formState.formValues;

	const classYearOrder = IndexForms.useOrderInput(ClassYearOrderFormValues.initial);
	const { data: classYearsData } = useClassYearLookupQuery({
		variables: { order: classYearOrder },
	});
	const classYear =
		classYearsData?.classYearConnection.edges.find((e) => e.node.graduationYear === graduationYear)?.node ?? null;

	const homeRoomSectionOrder = IndexForms.useOrderInput(HomeRoomSectionOrderFormValues.initial);
	const { loading: loadingHomeRoomSections, data: homeRoomSectionsData } = useHomeRoomSectionSelectQuery({
		variables: {
			filters: {
				academicTermIDEQ: academicTermID,
				homeRoom: [{ classYearIDEQ: classYear?.id }],
			},
			order: homeRoomSectionOrder,
		},
	});
	const homeRoomSectionOptions = React.useMemo(
		() => HomeRoomSectionSelect.toOptions(homeRoomSectionsData?.homeRoomSectionConnection.edges),
		[homeRoomSectionsData],
	);
	const someHomeRoomSections = (homeRoomSectionsData?.homeRoomSectionConnection.edges.length ?? 0) > 0;
	const noHomeRoomSections = !loadingHomeRoomSections && !someHomeRoomSections;

	const { graduationYear: graduationYearWasChanged } = formState.formChanged;
	const { homeRoomSectionID: clearHomeRoomSectionID, courseSectionIDs: clearCourseSectionIDs } =
		formState.formClearFunctions;
	React.useEffect(() => {
		if (academicTermIDWasChanged || graduationYearWasChanged) {
			clearHomeRoomSectionID();
			clearCourseSectionIDs();
		}
	}, [
		academicTermID,
		graduationYear,
		academicTermIDWasChanged,
		graduationYearWasChanged,
		clearHomeRoomSectionID,
		clearCourseSectionIDs,
	]);

	return {
		someHomeRoomSections,
		loadingHomeRoomSections,
		homeRoomSectionOptions,
		noHomeRoomSections,
		classYear,
	};
}

type AcademicSectionFieldProps = {
	academicTermID: EnrollmentFormValues["academicTermID"];
	formState: FormState<Pick<EnrollmentFormValues, "homeRoomSectionID" | "courseSectionIDs">>;
	academicTerm: AcademicTermsDatesListQuery["academicTermConnection"]["edges"][0]["node"] | null;
	classYear: ClassYearLookupQuery["classYearConnection"]["edges"][0]["node"] | null;
	variant: "create" | "create-update" | "on-existing";
} & Partial<ClassNameProps & StyleProps>;

function CourseSectionsField({
	academicTermID,
	formState,
	classYear,
	variant,
	className,
	style,
}: AcademicSectionFieldProps) {
	useInternalField(formState, "courseSectionIDs");

	const filters: CourseSectionFilterInput = {
		academicTermIDEQ: academicTermID,
	};
	if (formState.formValues.homeRoomSectionID) {
		filters.or = [
			{ homeRoomSectionIDEQ: formState.formValues.homeRoomSectionID },
			{
				homeRoomSectionIDIsNull: true,
				course: [{ classYears: [{ idEQ: classYear?.id }] }],
			},
		];
	} else {
		filters.course = [{ classYears: [{ idEQ: classYear?.id }] }];
	}

	const defaultOrder = IndexForms.useOrderInput(CourseSectionOrderFormValues.initial);
	const [loadOptions, { data }] = useCourseSectionSummaryListLazyQuery({
		variables: { filters, order: defaultOrder },
		notifyOnNetworkStatusChange: true,
	});

	const isEditing = formState.formEditing.courseSectionIDs;
	React.useEffect(() => {
		if (isEditing) {
			loadOptions();
		}
	}, [isEditing, loadOptions]);

	const { courseSectionIDs: setCourseSectionIDs } = formState.formSetFunctions;
	React.useEffect(() => {
		if ((variant === "on-existing" || variant === "create-update") && !formState.formChanged.homeRoomSectionID) {
			setCourseSectionIDs(formState.initialFormValues.courseSectionIDs);
			return;
		}

		if (data && formState.formValues.homeRoomSectionID) {
			setCourseSectionIDs(
				filterMap(
					data?.courseSectionConnection.edges ?? [],
					(e) => e.node.homeRoomSection?.id === formState.formValues.homeRoomSectionID,
					(e) => e.node.id,
				),
			);
		}
	}, [
		variant,
		formState.formChanged.homeRoomSectionID,
		formState.initialFormValues.courseSectionIDs,
		data,
		formState.formValues.homeRoomSectionID,
		setCourseSectionIDs,
	]);

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const selectedCourseSections = filterMap(
		data?.courseSectionConnection.edges ?? [],
		(e) => formState.formValues.courseSectionIDs.includes(e.node.id),
		(e) => e.node,
	);

	return (
		<Column justify="spaced-start" verticalSpacing="0.25rem" className={className} style={style}>
			<Row justify="space-between" align="center">
				<Label>Courses</Label>

				<Button variant="link" size="small" onClick={openModal}>
					<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
						<Icon.PencilSimple size={14} /> Edit Course Selection
					</Row>
				</Button>
			</Row>

			<Column justify="spaced-start" style={{ marginBottom: "0.75rem" }}>
				{selectedCourseSections.map((courseSection) => (
					<Card key={courseSection.id}>
						<CourseSectionComponents.CourseSectionSummaryView courseSection={courseSection} />
					</Card>
				))}
			</Column>

			<Button variant="link" size="small" onClick={openModal}>
				<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
					Add Courses <Icon.CaretRight size={14} />
				</Row>
			</Button>

			<Modal.If condition={isModalOpen}>
				<CourseSectionsModal formState={formState} data={data} onClose={closeModal} />
			</Modal.If>
		</Column>
	);
}

type CourseSectionsModalProps = RequiredKeys<
	Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">,
	"onClose"
> & {
	formState: FormState<Pick<EnrollmentFormValues, "homeRoomSectionID" | "courseSectionIDs">>;
	data: CourseSectionSummaryListQuery | null | undefined;
};

function CourseSectionsModal({ formState, data, ifRef, onClose, className, style }: CourseSectionsModalProps) {
	const { courseSectionIDs } = formState.formValues;
	const { date, events } = React.useMemo(() => {
		const startDate = addTimeToDate(dateTrunc(new Date(), "week"), [1, "day"]).toDate(); // start monday
		const endDate = addTimeToDate(startDate, [4, "days"]).toDate();

		if (!data) {
			return { date: startDate, events: [] };
		}

		const startDateISO = startDate.toISOString();
		const endDateISO = endDate.toISOString();

		const periods = [];
		const selectedCourseSectionEdges = data.courseSectionConnection.edges.filter((e) =>
			courseSectionIDs.includes(e.node.id),
		);
		for (let i = 0; i < selectedCourseSectionEdges.length; i++) {
			const { courseSectionPeriods, ...courseSection } = selectedCourseSectionEdges[i].node;
			periods.push(
				...courseSectionPeriods.map((e) => ({
					...e,
					courseSection: { ...courseSection, academicTerm: { startDate: startDateISO, endDate: endDateISO } },
				})),
			);
		}

		const events = CourseSectionPeriodUtilities.toEventCalendarItems(periods, startDate, endDate, true);

		return { date: startDate, events };
	}, [data, courseSectionIDs]);

	return (
		<Modal size="large" ifRef={ifRef} onClose={onClose} className={className} style={style}>
			<Modal.Header className={styles["course-sections-modal__header"]}>
				<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
					<Button variant="link" size="small" onClick={onClose} className="button--icon">
						<Icon.CaretLeft size={24} className="block" />
					</Button>

					<Heading.H2 noMargin>Select Courses</Heading.H2>
				</Row>
			</Modal.Header>
			<Modal.Body className={styles["course-sections-modal__body"]}>
				<Row style={{ height: "100%" }}>
					<Column justify="spaced-start" className={styles["course-sections-modal__course-sections-column"]}>
						<span className={styles["course-sections-modal__label"]}>Select Courses</span>

						{data?.courseSectionConnection.edges.map(({ node: courseSection }) => {
							const isSelected = courseSectionIDs.includes(courseSection.id);

							return (
								<label key={courseSection.id} htmlFor={courseSection.id} style={{ width: "100%" }}>
									<Card
										style={{
											width: "100%",
											borderColor: isSelected ? "var(--theme-colors__blue--light)" : undefined,
										}}
									>
										<Row justify="spaced-start" align="center" style={{ width: "100%" }}>
											<CheckBoxInput
												id={courseSection.id}
												value={isSelected}
												setValue={(value) =>
													formState.formSetFunctions.courseSectionIDs((prev) =>
														value ? [...prev, courseSection.id] : prev.filter((e) => e !== courseSection.id),
													)
												}
												style={{ flexShrink: 0 }}
											/>

											<CourseSectionComponents.CourseSectionSummaryView
												courseSection={courseSection}
												style={{ flexGrow: 1, width: "100%", minWidth: 0 }}
											/>
										</Row>
									</Card>
								</label>
							);
						})}
					</Column>

					<Column justify="spaced-start" className={styles["course-sections-modal__calendar-column"]}>
						<span className={styles["course-sections-modal__label"]}>Student Schedule</span>

						<BigEventCalendar.BusinessWeekView
							value={date}
							events={events}
							EventTooltip={Null}
							hideDateInDayLabel
							noItemIcon
							style={{ flexGrow: 1, minHeight: 0 }}
						/>
					</Column>
				</Row>
			</Modal.Body>
			<Modal.Footer className={styles["course-sections-modal__footer"]}>
				<Row justify="flex-end">
					<Button variant="primary" size="small" onClick={onClose}>
						Save Course Selection
					</Button>
				</Row>
			</Modal.Footer>
		</Modal>
	);
}

function Null() {
	return null;
}
